/**
 * PURPOSE: Reorganizar grupos con impacto explícito (FTR-0028 WG3, §15 del encargo; D-19).
 * RESPONSIBILITIES: Calcular el plan, mostrarlo entero —incluida la decisión de retirar— y
 *   ejecutarlo EXACTAMENTE como se aprobó.
 *
 *   LA REGLA QUE SOSTIENE TODO ESTE PANEL: se envía el `planHash` del plan que el administrador está
 *   viendo, nunca uno recalculado. Por eso, en cuanto cambia algo que forma parte del plan —la
 *   configuración objetivo o la confirmación de retiradas— el plan mostrado se DESCARTA y "Ejecutar"
 *   se apaga hasta volver a calcularlo. `confirmRemovals` entra en el hash a propósito: aprobar un
 *   plan que no retira nada y ejecutar uno que retira tres partidos es aritméticamente imposible.
 *
 *   Un `409 REORGANIZE_PLAN_STALE` significa que el borrador cambió entre medias. No se reintenta:
 *   se avisa, se recarga y se vuelve a revisar. Reintentar en silencio aplicaría algo que nadie vio.
 *
 *   EL BLOQUEO ES PUNTUAL, NO GLOBAL (§12.5). Un partido con actividad competitiva no cancela la
 *   operación: se queda como está y el resto se aplica.
 * COLLABORATORS: ProgrammingCenterProvider.jsx, services/programmingCenterService.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/panels/ReorganizePanel.jsx
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiPlay, FiRefreshCw, FiUsers } from "react-icons/fi";
import { toast } from "react-hot-toast";

import { useProgrammingCenter } from "../ProgrammingCenterProvider.jsx";
import { previewReorganization } from "../../../../services/programmingCenterService.js";
import { programmingCenterErrorMessage } from "../../../../utils/programmingCenterErrors.js";
import { groupLetter } from "../../../../utils/programmingCenterModel.js";
import SidePanel from "../components/SidePanel.jsx";

const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-800 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

const Bucket = ({ label, value, tone = "slate", hint = null }) => (
  <div
    className={`rounded-xl border px-3 py-2 ${
      tone === "green"
        ? "border-emerald-200 bg-emerald-50"
        : tone === "amber"
          ? "border-amber-200 bg-amber-50"
          : tone === "red"
            ? "border-rose-200 bg-rose-50"
            : "border-slate-200 bg-slate-50"
    }`}
  >
    <p className="text-lg font-black text-slate-900">{value}</p>
    <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">{label}</p>
    {hint ? <p className="mt-0.5 text-[11px] font-bold text-slate-400">{hint}</p> : null}
  </div>
);

const ReorganizePanel = ({ open, onClose }) => {
  const { draft, scope, actions, busy } = useProgrammingCenter();
  const current = draft?.draft || null;

  const [form, setForm] = useState({ teamsTotal: "", maxPerGroup: "4", rounds: "1" });
  const [confirmRemovals, setConfirmRemovals] = useState(false);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setPlan(null);
      setConfirmRemovals(false);
      return;
    }
    setForm({
      teamsTotal: String(current?.teams_total || ""),
      maxPerGroup: String(current?.max_per_group || 4),
      rounds: String(current?.rounds || 1),
    });
  }, [open, current]);

  const config = useMemo(
    () => ({
      teamsTotal: Number(form.teamsTotal) || 0,
      maxPerGroup: Number(form.maxPerGroup) || 0,
      rounds: Number(form.rounds) || 1,
      finalStartStage: current?.final_start_stage || null,
      slotMinutes: current?.slot_minutes || 60,
    }),
    [form, current]
  );

  /**
   * Cualquier cambio que forme parte del plan lo invalida. No es una precaución de interfaz: el
   * `planHash` del backend cubre la configuración Y la decisión de retirar, así que un plan mostrado
   * con otros valores ya no es el plan que se ejecutaría.
   */
  const invalidatePlan = () => setPlan(null);

  const runPreview = async () => {
    setLoading(true);
    try {
      const data = await previewReorganization({ ...scope, config, confirmRemovals });
      // Se guarda junto a la decisión con la que se calculó: es lo único que se puede ejecutar.
      setPlan({ data, confirmRemovals, config });
    } catch (error) {
      setPlan(null);
      toast.error(programmingCenterErrorMessage(error, "Plan de reorganización"));
    } finally {
      setLoading(false);
    }
  };

  const run = async () => {
    if (!plan?.data?.summary?.planHash) return;
    const result = await actions.applyReorganize({
      config: plan.config,
      planHash: plan.data.summary.planHash,
      confirmRemovals: plan.confirmRemovals,
    });
    if (result.ok) {
      toast.success("Reorganización aplicada.");
      setPlan(null);
      onClose();
      return;
    }
    // El plan ya no vale: se descarta para que no pueda ejecutarse a ciegas.
    if (result.error?.code === "REORGANIZE_PLAN_STALE") setPlan(null);
  };

  const summary = plan?.data?.summary || null;
  const detail = plan?.data?.plan || null;
  const disabled = loading || Boolean(busy);
  const stale = Boolean(plan) && plan.confirmRemovals !== confirmRemovals;

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Reorganizar grupos"
      subtitle="Cambia la distribución mostrando primero qué pasa con los partidos que ya existen"
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">
            Equipos
          </span>
          <input
            type="number"
            min="2"
            value={form.teamsTotal}
            onChange={(event) => {
              setForm((f) => ({ ...f, teamsTotal: event.target.value }));
              invalidatePlan();
            }}
            className={inputClass}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">
            Máx. por grupo
          </span>
          <input
            type="number"
            min="2"
            max="6"
            value={form.maxPerGroup}
            onChange={(event) => {
              setForm((f) => ({ ...f, maxPerGroup: event.target.value }));
              invalidatePlan();
            }}
            className={inputClass}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">
            Rondas
          </span>
          <select
            value={form.rounds}
            onChange={(event) => {
              setForm((f) => ({ ...f, rounds: event.target.value }));
              invalidatePlan();
            }}
            className={inputClass}
          >
            <option value="1">Una ronda</option>
            <option value="2">Ida y vuelta</option>
          </select>
        </label>
      </div>

      <label className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-bold text-amber-900">
        <input
          type="checkbox"
          checked={confirmRemovals}
          onChange={(event) => setConfirmRemovals(event.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-400"
        />
        <span>
          Retirar los partidos que dejan de existir
          <span className="block text-[11px] font-bold text-amber-800">
            Solo los que no tienen actividad competitiva. Sin esta confirmación sus filas se marcan
            huérfanas y los partidos se quedan como están.
          </span>
        </span>
      </label>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={runPreview}
          disabled={disabled || !config.teamsTotal}
          className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-black text-slate-700 transition hover:border-blue-300 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FiRefreshCw className="text-sm" />
          {plan ? "Recalcular plan" : "Calcular plan"}
        </button>
        <button
          type="button"
          onClick={run}
          disabled={disabled || !plan || stale || !summary?.applicable}
          title={
            stale
              ? "Cambiaste la confirmación de retiradas: vuelve a calcular el plan antes de ejecutarlo."
              : undefined
          }
          className="flex items-center gap-2 rounded-lg bg-amber-600 px-3 py-2 text-sm font-black text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FiPlay className="text-sm" />
          Ejecutar el plan aprobado
        </button>
      </div>

      {stale ? (
        <p className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-black text-amber-800">
          Cambiaste la decisión de retirar. Vuelve a calcular el plan: se ejecuta exactamente el que
          se muestra.
        </p>
      ) : null}

      {plan && summary ? (
        <section className="mt-4 flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Bucket label="Se mantienen" value={summary.keep} tone="green" />
            <Bucket label="Se mueven" value={summary.moved} hint="Cambia la estructura, no la hora" />
            <Bucket label="Se crean" value={summary.create} tone="green" />
            <Bucket label="Huérfanas" value={summary.orphan} tone="amber" />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Bucket
              label="Se retirarán"
              value={summary.willRetire}
              tone={summary.willRetire ? "red" : "slate"}
              hint={`${summary.retire} candidatos`}
            />
            <Bucket
              label="Bloqueados"
              value={summary.blocked}
              tone={summary.blocked ? "amber" : "slate"}
              hint="Con actividad competitiva"
            />
            <Bucket label="Reestructurados" value={summary.restructure} />
            <Bucket
              label="Sin partido"
              value={summary.detach}
              hint="Solo se marcan huérfanas"
            />
          </div>

          {detail?.stranded?.length ? (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3">
              <p className="flex items-center gap-2 text-sm font-black text-amber-800">
                <FiUsers className="text-sm" />
                {detail.stranded.length} participantes quedan en slots que desaparecen
              </p>
              <p className="mt-1 text-xs font-bold text-amber-800">
                El sorteo sigue siendo la autoridad: el Centro no los reasigna solo. Reubícalos desde
                el sorteo o desde «Asignar institución al slot».
              </p>
              <p className="mt-2 text-xs font-black text-amber-900">
                {detail.stranded
                  .slice(0, 12)
                  .map(
                    (item) =>
                      item.label || `${groupLetter(item.idgrupo)}${item.nro_grupo ?? ""}`
                  )
                  .join(" · ")}
              </p>
            </div>
          ) : null}

          {detail?.blocked?.length ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="flex items-center gap-2 text-xs font-black uppercase tracking-wide text-slate-600">
                <FiAlertTriangle className="text-xs" />
                Bloqueados (el resto sí se aplica)
              </p>
              <p className="mt-1 text-xs font-bold text-slate-600">
                {detail.blocked
                  .slice(0, 12)
                  .map((item) => item.draft_key)
                  .join(" · ")}
              </p>
            </div>
          ) : null}

          {!summary.applicable ? (
            <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600">
              Esta configuración no cambia nada: no hay plan que ejecutar.
            </p>
          ) : null}

          <p className="text-[11px] font-bold text-slate-400">
            Plan {summary.planHash?.slice(0, 12)}… · Lo que sobreviva conserva fecha, hora, sede,
            cancha y su partido.
          </p>
        </section>
      ) : null}
    </SidePanel>
  );
};

export default ReorganizePanel;
